/**
 * F-506-FE-03: DashboardPage
 *
 * Doctor dashboard at /dashboard. Three sections:
 * 1. Start New Session  — dominant CTA, navigates to /session/new
 * 2. Recent Completed   — top 3 most recent completed notes, always visible
 * 3. Pending Notes      — DRAFT notes with age indicator, tap to resume
 *
 * Data: GET /api/notes?status=COMPLETED and GET /api/notes?status=DRAFT
 *
 * Design: Clear hierarchy. Start Session CTA dominates. Mobile-first.
 * RTL support via CSS logical properties.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { NoteListItem, NotesListResponse } from '@/features/notes/types';
import { CompletedNoteCard } from '@/features/notes/CompletedNoteCard';
import { useCopyToEmr } from '@/features/notes/useCopyToEmr';
import { useExportPdf } from '@/features/notes/useExportPdf';
import { useSaveAsTxt } from '@/features/notes/useSaveAsTxt';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatAge(dateString: string): string {
  const now = Date.now();
  const then = new Date(dateString).getTime();
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} min${diffMins === 1 ? '' : 's'} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  return new Date(dateString).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
  });
}

function formatSessionDateShort(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

// ---------------------------------------------------------------------------
// Start Session CTA
// ---------------------------------------------------------------------------

function StartSessionCard() {
  const navigate = useNavigate();
  return (
    <section aria-labelledby="start-session-heading">
      <button
        onClick={() => navigate('/session/new')}
        className="w-full group relative overflow-hidden rounded-clinical shadow-clinical-md
                   bg-clinical-charcoal text-white
                   min-h-[var(--touch-target-critical)]
                   p-8 flex flex-col items-start gap-3
                   transition-all duration-200 hover:bg-clinical-charcoal-soft hover:shadow-clinical-lg
                   focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clinical-amber focus-visible:ring-offset-2"
        aria-label="Start a new clinical consultation session"
      >
        {/* Background accent */}
        <div
          className="absolute inset-0 opacity-5 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse 80% 60% at 90% 50%, #D4A574 0%, transparent 70%)',
          }}
          aria-hidden="true"
        />

        <div className="relative flex items-center gap-4 w-full">
          <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 transition-colors duration-200 group-hover:bg-white/15">
            <svg
              className="w-6 h-6 text-white"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
          </div>
          <div className="flex-1 text-start">
            <h2
              id="start-session-heading"
              className="font-heading text-xl font-semibold text-white leading-snug"
            >
              Start New Session
            </h2>
            <p className="text-sm font-body text-white/70 mt-0.5">
              Begin a bilingual consultation
            </p>
          </div>
          <svg
            className="w-5 h-5 text-white/50 flex-shrink-0 transition-transform duration-200 group-hover:translate-x-1"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
          </svg>
        </div>
      </button>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Recent Completed section — per-card wrapper owns its own hook instances
// ---------------------------------------------------------------------------

interface CompletedCardWrapperProps {
  note: NoteListItem;
  isExportingPdf: boolean;
  onExportPdf: () => void;
  isSavingTxt: boolean;
  onSaveTxt: () => void;
}

function CompletedCardWrapper({
  note,
  isExportingPdf,
  onExportPdf,
  isSavingTxt,
  onSaveTxt,
}: CompletedCardWrapperProps) {
  const navigate = useNavigate();
  const { state: copyState, copy: copyToEmr } = useCopyToEmr();

  return (
    <CompletedNoteCard
      note={note}
      onCopy={() => void copyToEmr(note.id)}
      copyState={copyState}
      onExportPdf={onExportPdf}
      isExportingPdf={isExportingPdf}
      onSaveTxt={onSaveTxt}
      isSavingTxt={isSavingTxt}
      onEdit={() => navigate(`/notes/${note.id}/edit`)}
    />
  );
}

// ---------------------------------------------------------------------------
// Toast (inline for dashboard use)
// ---------------------------------------------------------------------------

interface DashboardToastProps {
  message: string;
  onDismiss: () => void;
}

function DashboardToast({ message, onDismiss }: DashboardToastProps) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 4_000);
    return () => clearTimeout(t);
  }, [onDismiss]);

  return (
    <div className="fixed bottom-6 end-6 z-50 max-w-sm clinical-card p-4 flex items-start gap-3 shadow-clinical-lg">
      <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
        <svg className="w-3 h-3 text-safety-green" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <div className="flex-1">
        <p className="text-sm font-body font-medium text-clinical-charcoal">{message}</p>
      </div>
      <button
        type="button"
        className="text-clinical-text-muted hover:text-clinical-charcoal transition-colors flex-shrink-0"
        onClick={onDismiss}
        aria-label="Dismiss"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------

function RecentCompletedSection() {
  // Backend now applies the hospital config retention cap automatically.
  // We read meta.noteRetentionHours from the envelope to render the correct subtitle.
  const { data: response, isLoading } = useQuery<NotesListResponse>({
    queryKey: ['notes', 'COMPLETED'],
    queryFn: () => api.get<NotesListResponse>('/api/notes?status=COMPLETED'),
  });
  const data = response;

  const { isExporting, exportPdf } = useExportPdf();
  const { state: saveTxtState, save: saveAsTxt } = useSaveAsTxt();
  const [toast, setToast] = useState<string | null>(null);

  const handleExportPdf = useCallback(
    (note: NoteListItem) => {
      void exportPdf(note.id, (msg) => setToast(msg));
    },
    [exportPdf],
  );

  const handleSaveTxt = useCallback(
    (note: NoteListItem) => {
      void saveAsTxt(note.id, note.templateName, note.completedAt ?? note.sessionDate);
    },
    [saveAsTxt],
  );

  // noteRetentionHours from the API envelope — hide the subtitle until loaded to avoid
  // flash-of-wrong-number (§3 decision: no placeholder duration during load).
  const noteRetentionHours = response?.meta?.noteRetentionHours;

  // Top 3 by completedAt ?? updatedAt descending
  const topNotes = (data?.data ?? [])
    .slice()
    .sort((a: NoteListItem, b: NoteListItem) => {
      const aTime = new Date(a.completedAt ?? a.updatedAt).getTime();
      const bTime = new Date(b.completedAt ?? b.updatedAt).getTime();
      return bTime - aTime;
    })
    .slice(0, 3);

  const totalCount = data?.data?.length ?? 0;

  return (
    <section aria-labelledby="recent-completed-heading">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div>
            <h2
              id="recent-completed-heading"
              className="font-heading text-lg font-semibold text-clinical-charcoal"
            >
              Recent Completed
            </h2>
            {/* Confidentiality subtitle — rendered only after load, with dynamic hours value */}
            {!isLoading && noteRetentionHours !== undefined && (
              <p className="text-xs font-body text-clinical-text-muted mt-0.5">
                Available for {noteRetentionHours} hours after completion.
              </p>
            )}
          </div>
          {totalCount > 0 && (
            <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-200 text-xs font-body font-semibold">
              {totalCount}
            </span>
          )}
        </div>
        <Link
          to="/notes/completed"
          className="text-sm font-body text-clinical-teal hover:text-clinical-charcoal transition-colors flex items-center gap-1"
        >
          View all
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
          </svg>
        </Link>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="clinical-card p-4 flex items-center gap-3 animate-pulse">
              <div className="w-2.5 h-2.5 skeleton rounded-full flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="skeleton h-4 rounded w-2/5" />
                <div className="skeleton h-3 rounded w-3/5" />
              </div>
              <div className="skeleton h-8 w-20 rounded flex-shrink-0" />
            </div>
          ))}
        </div>
      ) : topNotes.length === 0 ? (
        <div className="clinical-card p-5 text-center">
          <p className="text-sm font-body text-clinical-text-muted">No completed notes yet.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {topNotes.map((note) => (
            <CompletedCardWrapper
              key={note.id}
              note={note}
              isExportingPdf={isExporting(note.id)}
              onExportPdf={() => handleExportPdf(note)}
              isSavingTxt={saveTxtState === 'saving'}
              onSaveTxt={() => handleSaveTxt(note)}
            />
          ))}
        </div>
      )}

      {/* Toast for PDF export confirmation */}
      {toast && <DashboardToast message={toast} onDismiss={() => setToast(null)} />}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Pending Notes section
// ---------------------------------------------------------------------------

interface PendingNoteCardProps {
  note: NoteListItem;
}

function PendingNoteCard({ note }: PendingNoteCardProps) {
  const navigate = useNavigate();
  const sessionDateStr = `${formatSessionDateShort(note.sessionDate)} · ${formatAge(note.createdAt)}`;

  return (
    <div className="clinical-card p-4 flex items-start gap-4">
      {/* Amber dot indicator */}
      <div className="flex-shrink-0 mt-1" aria-hidden="true">
        <div className="w-2.5 h-2.5 rounded-full bg-clinical-amber" />
      </div>

      {/* Main tappable content area */}
      <button
        type="button"
        className="flex-1 min-w-0 text-start focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clinical-amber focus-visible:ring-offset-2 rounded-sm"
        onClick={() => navigate(`/notes/${note.id}/edit`)}
        aria-label={`Resume note for ${note.templateName} — ${sessionDateStr}`}
      >
        <p className="font-body font-semibold text-clinical-charcoal text-sm truncate">
          {note.templateName}
        </p>
        <p className="text-xs font-body text-clinical-text-muted truncate mt-0.5">
          {sessionDateStr}
        </p>
        {note.patientRef && (
          <p className="text-xs font-body text-clinical-text-muted truncate mt-0.5">
            {note.patientRef}
          </p>
        )}
        <span className="status-draft mt-1 inline-block">Draft</span>
      </button>

      {/* Explicit Edit button anchored to row end */}
      <button
        type="button"
        className="btn-ghost px-3 py-1.5 text-xs min-h-[40px] flex items-center gap-1 flex-shrink-0"
        onClick={() => navigate(`/notes/${note.id}/edit`)}
        aria-label={`Edit note for ${note.templateName}`}
      >
        <DashboardPencilIcon />
        Edit
      </button>
    </div>
  );
}

function PendingNotesSection() {
  const { data, isLoading } = useQuery<{ data: NoteListItem[] }>({
    queryKey: ['notes', 'DRAFT'],
    queryFn: () => api.get<{ data: NoteListItem[] }>('/api/notes?status=DRAFT'),
  });

  const notes = data?.data ?? [];

  return (
    <section aria-labelledby="pending-notes-heading">
      <div className="flex items-center justify-between mb-3">
        <h2
          id="pending-notes-heading"
          className="font-heading text-lg font-semibold text-clinical-charcoal"
        >
          Pending Notes
        </h2>
        {notes.length > 0 && (
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-clinical-amber text-white text-xs font-body font-semibold">
            {notes.length}
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="clinical-card p-4 flex items-start gap-4">
              <div className="skeleton w-2.5 h-2.5 mt-1 rounded-full" />
              <div className="flex-1 space-y-2">
                <div className="skeleton h-4 rounded w-3/5" />
                <div className="skeleton h-3 rounded w-2/5" />
              </div>
            </div>
          ))}
        </div>
      ) : notes.length === 0 ? (
        <div className="clinical-card p-6 text-center">
          <div className="w-10 h-10 rounded-full bg-clinical-cream-dark flex items-center justify-center mx-auto mb-3">
            <svg
              className="w-5 h-5 text-clinical-text-muted"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25Z"
              />
            </svg>
          </div>
          <p className="font-body font-medium text-clinical-charcoal text-sm">
            All caught up
          </p>
          <p className="text-xs font-body text-clinical-text-muted mt-1">
            No pending notes. Start a session to generate your next note.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {notes.map((note) => (
            <PendingNoteCard key={note.id} note={note} />
          ))}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function DashboardPage() {
  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-8">
      {/* Start Session — hero action */}
      <StartSessionCard />

      {/* Recent Completed — top 3 peek */}
      <RecentCompletedSection />

      {/* Pending Notes section is permanently hidden from the dashboard.
          PendingNotesPage at /notes/pending remains reachable by direct URL. */}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline icons (pending section)
// ---------------------------------------------------------------------------

function DashboardPencilIcon() {
  return (
    <svg
      className="w-3 h-3"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125"
      />
    </svg>
  );
}
